import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import BottomSheet from './BottomSheet';

const API_URL = '/api';

/**
 * Bottom Sheet фильтров
 */
const FilterModal = ({
  isOpen,
  onClose,
  currentCategoryId,
  // Значения по умолчанию здесь: React 19 игнорирует defaultProps
  categories = [],
  selectedTags = [],
  onApplyFilters,
  onCategoryChange,
}) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [localSelectedTags, setLocalSelectedTags] = useState(new Set(selectedTags || []));
  const [loading, setLoading] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const [resultsCount, setResultsCount] = useState(null);
  const tagsCache = useRef({});
  const loadedCategoryRef = useRef(null);
  // Счётчик запросов подсчёта — чтобы ответ на устаревший не перезаписал свежий
  const countRequestRef = useRef(0);

  // Загрузка тегов категории
  useEffect(() => {
    if (isOpen && currentCategoryId) {
      // Если теги уже загружены для этой категории, использовать кеш
      if (tagsCache.current[currentCategoryId]) {
        setAvailableTags(tagsCache.current[currentCategoryId]);
        loadedCategoryRef.current = currentCategoryId;
      } else {
        loadTags();
      }
    }
    // loadTags пересоздаётся каждый рендер — в deps даст бесконечный refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentCategoryId]);

  // Синхронизация локального состояния с props
  useEffect(() => {
    setLocalSelectedTags(new Set(selectedTags || []));
  }, [selectedTags]);

  // Подсчет результатов при изменении выбранных тегов
  useEffect(() => {
    if (isOpen && currentCategoryId && localSelectedTags.size > 0) {
      // 250мс вместо 500: полсекунды со старым числом на кнопке читаются
      // как «показывает неправду». Быстрым тапам дебаунс всё ещё мешает
      // слать лишние запросы
      const timer = setTimeout(() => {
        countResults();
      }, 250);

      return () => clearTimeout(timer);
    } else if (isOpen && localSelectedTags.size === 0) {
      // Фильтров не осталось — считать нечего, кнопка возвращается
      // к «Показать результаты»
      setResultsCount(null);
    }
    // countResults пересоздаётся каждый рендер — см. выше
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSelectedTags, isOpen, currentCategoryId]);

  const loadTags = async () => {
    // Пропустить если уже загружаем для той же категории
    if (loadedCategoryRef.current === currentCategoryId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/categories/${currentCategoryId}/tags`);
      if (!response.ok) throw new Error('Failed to load tags');

      const result = await response.json();
      const tags = result.data || [];

      setAvailableTags(tags);
      tagsCache.current[currentCategoryId] = tags;
      loadedCategoryRef.current = currentCategoryId;
    } catch (error) {
      setAvailableTags([]);
    } finally {
      setLoading(false);
    }
  };

  const countResults = async () => {
    // Номер запроса: ответы приходят не в том порядке, в каком уходили,
    // и медленный ранний мог перезаписать число от более позднего —
    // на кнопке оставался счёт от предыдущего набора фильтров
    countRequestRef.current += 1;
    const requestId = countRequestRef.current;

    setCountLoading(true);
    try {
      const params = new URLSearchParams({
        'filters[category][id][$eq]': currentCategoryId,
        'filters[published][$eq]': 'true',
        'pagination[page]': '1',
        'pagination[pageSize]': '1',
      });

      const tagIds = Array.from(localSelectedTags);
      tagIds.forEach((tagId, index) => {
        params.append(`filters[tags][id][$in][${index}]`, tagId);
      });

      const response = await fetch(`${API_URL}/places/filtered?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to count results');

      const result = await response.json();
      if (requestId !== countRequestRef.current) return;

      setResultsCount(result.meta?.pagination?.total || 0);
    } catch (error) {
      if (requestId !== countRequestRef.current) return;

      // Оборванный запрос — не «ноль мест»: 0 показал бы «Нет результатов»
      // и заблокировал кнопку, хотя места есть. Счётчик не трогаем совсем —
      // применить фильтры пользователь всё равно может
    } finally {
      if (requestId === countRequestRef.current) setCountLoading(false);
    }
  };

  const handleToggleTag = useCallback((tagId) => {
    setLocalSelectedTags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tagId)) {
        newSet.delete(tagId);
      } else {
        newSet.add(tagId);
      }
      return newSet;
    });
  }, []);

  const handleApply = useCallback(() => {
    onApplyFilters(Array.from(localSelectedTags));
    onClose();
  }, [localSelectedTags, onApplyFilters, onClose]);


  const selectedCount = localSelectedTags.size;

  /**
   * Кнопка применения.
   *
   * Надпись прежняя, одной строкой по центру: «Показать 12 результатов».
   * Дёрганья лечатся не разбором строки на части, а тем, что промежуточных
   * состояний больше нет:
   *
   * 1. «Подсчёт…» со спиннером убран. Это была отдельная строка своей
   *    ширины между двумя осмысленными — половина скачков приходилась на неё
   * 2. Пока идёт пересчёт, на кнопке остаётся **прошлое** число, а не пустая
   *    надпись. Строка меняется один раз — когда пришёл ответ
   * 3. На время пересчёта надпись приглушается: подсказка, что число ещё
   *    уточняется, вместо подмены текста
   *
   * Табличные цифры не дают ширине скакать на 1 → 10 → 100.
   */
  // Показывать нечего — кнопка неактивна
  const nothingToShow = selectedCount > 0 && resultsCount === 0;

  const applyButton = availableTags.length > 0 ? (
    <button
      onClick={handleApply}
      // Блокируется только когда показывать нечего. Во время пересчёта
      // кнопка живая: ждать подсчёта, чтобы применить фильтр, незачем
      disabled={nothingToShow}
      className="w-full rounded-md h-12 px-4 text-label tabular flex items-center justify-center text-center transition-[background-color,color,opacity,transform] duration-fast ease-out active:scale-98 disabled:cursor-not-allowed"
      style={{
        // Неактивная — серая поверхностью, а не приглушённая синяя:
        // выцветший акцент читается как «кнопка грузится», а не «нельзя»
        background: nothingToShow ? 'var(--color-card-secondary)' : 'var(--color-action)',
        color: nothingToShow ? 'var(--color-text-hint)' : 'var(--color-on-action)',
        // Пока идёт пересчёт, надпись чуть приглушена: видно, что число
        // ещё уточняется, но текст на месте и ничего не прыгает
        opacity: countLoading ? 0.75 : 1,
      }}
    >
      {nothingToShow
        ? 'Нет результатов'
        : selectedCount > 0 && resultsCount !== null
        ? `Показать ${resultsCount} ${
            resultsCount === 1
              ? 'результат'
              : resultsCount < 5
              ? 'результата'
              : 'результатов'
          }`
        : 'Показать результаты'}
    </button>
  ) : null;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Фильтры"
      footer={applyButton}
      // Панель с выбором: тап мимо сбросил бы отмеченные теги
      closeOnBackdrop={false}
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div
            className="w-8 h-8 rounded-full animate-spin mb-3"
            style={{ border: '2px solid var(--color-border)', borderTopColor: 'var(--color-action)' }}
          ></div>
          <p className="text-body-sm text-theme-text-hint">Загрузка фильтров…</p>
        </div>
      ) : availableTags.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-body text-theme-text-hint">Для этой категории фильтры не настроены</p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 pb-1">
          {availableTags.map((tag) => {
            const isSelected = localSelectedTags.has(tag.id);
            const iconUrl = tag.icon?.url || null;

            return (
              <button
                key={tag.id}
                onClick={() => handleToggleTag(tag.id)}
                aria-pressed={isSelected}
                // transition-colors, не transition-all: анимировать нужно
                // только заливку, размеры у обоих состояний равны
                className="flex items-center gap-2 px-4 py-2 rounded-md transition-colors duration-fast ease-out h-[40px]"
                style={isSelected ? {
                  backgroundColor: 'var(--color-action)',
                  color: 'var(--color-on-action)',
                } : {
                  backgroundColor: 'var(--color-card-secondary)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {iconUrl && (
                  <img
                    src={iconUrl}
                    alt=""
                    aria-hidden="true"
                    className="w-5 h-5 flex-shrink-0"
                    style={{ filter: isSelected ? 'brightness(0) invert(1)' : 'none' }}
                  />
                )}
                <span className="text-label whitespace-nowrap leading-tight">{tag.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </BottomSheet>
  );
};

FilterModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentCategoryId: PropTypes.number,
  categories: PropTypes.array,
  selectedTags: PropTypes.arrayOf(PropTypes.number),
  onApplyFilters: PropTypes.func.isRequired,
  onCategoryChange: PropTypes.func,
};

export default FilterModal;
